import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useDebounce } from "use-debounce";
import { IEntity } from "dto/IEntity";
import casesService from "api/casesService";
import ClientIcon from "assets/icons/person.svg?react";
import NewInput from "components/NewInput";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useOnClickOutside } from "usehooks-ts";
import { ITag } from "dto/ITag";
import { Avatar, Tooltip } from "@mui/material";
import InfoIcon from "assets/icons/info_icon.svg?react";
import { IDropdownObject } from "components/Dropdown/typings";
import { convertToLetters } from "utils/convertToLetters";
import DropdownMui from "components/DropdownMui";
import CaseParticipantEditorModal from "components/CaseParticipantEditorModal";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import Popper from "components/Popper";

const EntityRow = ({
  participant,
  afterEdit,
  setIsModified,
  allTags,
}: {
  participant: IEntity;
  onRemove?: (participant: IEntity) => void;
  afterEdit: (participant: ICaseParticipant) => void;
  setIsModified: (value: boolean) => void;
  allTags: ITag[];
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const [isEditing] = useState<boolean>(false);
  const [name] = useState<string>(participant.Label);
  const [showInfo, setShowInfo] = useState(false);
  const refWrapper = useRef();
  const refEditInput = useRef();

  const Rlist = allTags
    ? allTags
        ?.filter((tag: ITag) => tag.Type === 3)
        ?.map((tag: ITag) => {
          return {
            name: tag.Name,
            value: tag.Id,
            AdditionalInfo:
              tag.Global && tag.Name === "Klient" ? (
                <div>
                  <Tooltip title={t("participants.clientRoleTooltip")}>
                    <div>
                      <InfoIcon></InfoIcon>
                    </div>
                  </Tooltip>
                </div>
              ) : null,
          };
        })
    : [];

  useOnClickOutside(refEditInput, () => {
    if (isEditing) {
      commitName(name);
    }
  });

  const setRole = (participant: IEntity, role?: IDropdownObject) => {
    participant.RoleId = role ? (role.value as string) : null;
    setIsModified(true);
  };

  const commitName = (val?: string) => {
    // console.log("Setting name", val ?? name);
    // participant.Name = val ?? name;
    // participant.Label = val ?? name;
    // afterEdit(participant);
    // setIsEditing(false);
    // setEditInline(false);
  };

  const handleUpdateParticipant = (p: ICaseParticipant) => {
    afterEdit(p);
  };

  return (
    <div className={styles.entityBox}>
      <div className={styles.entity}>
        <Avatar className={styles.avatar}>
          {convertToLetters(participant?.Label ?? "")}
        </Avatar>
        <div className={styles.labelWrapper}>
          <div className={styles.label}>{participant?.Label ?? ""} </div>
          <div className={styles.icon}>
            <Tooltip title={t("entities.entityData")}>
              <div>
                <InfoIcon
                  onClick={(e) => {
                    setShowInfo(!showInfo);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                ></InfoIcon>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className={styles.boxes}>
        <div className={styles.box}>
          <DropdownMui
            variant="standard"
            size="small"
            hideBorder={true}
            defaultValue={participant.RoleId ?? ""}
            className={styles.parcelDropdown}
            onChange={(r) => setRole(participant, r)}
            list={Rlist}
            placeholder={t("common.none")}
            allowNull={true}
          />
        </div>
        {/* <div className={styles.box}>
                    <Dropdown
                      defaultValue={participant.DefaultParcelType}
                      className={styles.parcelDropdown}
                      onChange={(r) => setParcel(participant, r)}
                      list={parcelTypes}
                      placeholder="Brak"
                      noBackground
                      allowNull
                      disableChange={!canEdit}
                    />
                  </div> */}

        <div className={styles.more} ref={refWrapper}>
          {showInfo && (
            <Popper
              open={showInfo}
              setOpen={setShowInfo}
              anchorEl={refWrapper?.current}
            >
              <CaseParticipantEditorModal
                toggle={(r) => setShowInfo(false)}
                source={participant as ICaseParticipant}
                update={handleUpdateParticipant}
              ></CaseParticipantEditorModal>
            </Popper>
          )}
        </div>
      </div>
    </div>
  );
};

const SearchEntitiesNew: FC<Props> = ({
  label,
  onChoose,
  onCreate,
  onClose,
  initOpen,
  tags,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [searchValue] = useDebounce(value, 300);
  const [searchList, setSearchList] = useState<IEntity[]>([]);
  const { data: entitiesData, refetch } =
    casesService.useGetAddressBook(searchValue);
  const entities: IEntity[] = useMemo(
    () => entitiesData?.data ?? [],
    [entitiesData]
  );

  const [active, setActive] = useState(false);
  const [deb, setDeb] = useState(false);

  useEffect(() => {
    if (!initOpen) return;
    setActive(true);
  }, []);

  useEffect(() => {
    if (active) {
      setSearchList(entities);
    }
  }, [entities, active]);

  useEffect(() => {
    if (!value.length) {
      setSearchList(entities);
      return;
    }
  }, [value]);

  useEffect(() => {
    if (active) {
      refetch();
    }
  }, [active]);

  const outsideRef = useRef(null);

  // useOnClickOutside(outsideRef, () => {
  //   if (active) {
  //     setActive(false);
  //   }

  //   if (onClose && !label) {
  //     onClose();
  //   }
  // });

  return (
    <div ref={outsideRef} className={styles.wrapper}>
      <div
        onClick={() => {
          setActive(!active);
          setSearchList([]);
          setValue("");
        }}
        className={styles.card}
      >
        <ClientIcon className={styles.icon} />
        {label ? label : t("participants.title")}

        {!onClose && (
          <FontAwesomeIcon
            className={styles.closeIcon}
            icon={active ? faCaretUp : faCaretDown}
          />
        )}
      </div>

      {active && (
        <div className={styles.dropdown}>
          <div className={styles.search}>
            <NewInput
              focus
              labelClassName={styles.x}
              label={t("entities.searchEntity")}
              className={styles.d}
              value={value}
              onChange={(text) => setValue(text)}
            />

            {/* <div
              onClick={() => {
                setDeb(!deb);
              }}
              className={styles.create}
            >
              Nowy podmiot
            </div> */}
          </div>
          <div className={styles.list}>
            {searchList.map((c) => (
              <div
                className={styles.clickable}
                onClick={() => {
                  onChoose(c);
                  setActive(false);
                  setSearchList([]);
                  setValue("");
                }}
              >
                <EntityRow
                  setIsModified={() => {}}
                  key={c.Id}
                  participant={c}
                  allTags={tags}
                  afterEdit={() => {}}
                  canEdit={true}
                ></EntityRow>
              </div>
            ))}
          </div>
        </div>
      )}

      {deb && (
        <div className={styles.debDropdown}>
          <div
            onClick={() => {
              setActive(false);
              setSearchList([]);
              setValue("");
              onCreate?.(false);
              setDeb(false);
            }}
            className={styles.deb}
          >
            {t("common.person")}
          </div>
          <div
            onClick={() => {
              setActive(false);
              setSearchList([]);
              setValue("");
              onCreate?.(true);
              setDeb(false);
            }}
            className={styles.deb}
          >
            {t("admin.company")}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchEntitiesNew;
